<template>
    <div
        class="stamp-holder"
        ref="root"
        v-if="stamp"
        @mouseenter="enableTooltip"
        @mouseleave="disableTooltip"

        @drop="handleDragDropEvents"
        @dragover.prevent="handleDragDropEvents"
        @dragleave.prevent="handleDragDropEvents"
    >
        <template v-if="stampShifts && resourceIndex != undefined && resource && unavailabilities">
            <StampShiftMarker
                v-for="(stampShift, shiftIndex) in stampShifts"
                :key="shiftIndex"
                :horizontalPosition="dayIndex"
                :verticalPosition="resourceIndex"
                :rosterId="rosterId"
                :rosterNames="rosterNames"
                :shiftIndex="shiftIndex"
                :stampShift="stampShift"
                :shift="shifts[stampShift.shiftId]"
                :day="day"
                :ref="'markers-' + dayIndex + '-' + resourceIndex"
                :editable="editable"
                :resource="resource"
                :fromOtherRoster="fromOtherRoster"
                :wish="wish"
                :unavailabilities="unavailabilities"
            />
        </template>
        <StampShiftAddButton
            v-if="isEmployed && editable && resourceIndex != undefined"
            :horizontalPosition="dayIndex"
            :verticalPosition="resourceIndex"
            :shift="false"
            :day="day"
            :shiftIndex="stampShifts.length"
        />
    </div>
</template>

<script lang="ts">
    import {Component, Vue, Prop, Ref} from 'vue-facing-decorator';
    import StampShiftMarker from '@/components/Planbord/StampShiftMarker.vue';
    import StampShiftAddButton from '@/components/Planbord/StampShiftAddButton.vue';
    import Tooltip from '@/components/Tooltip/Tooltip.vue';
    import KeyboardDeploymentAutocomplete from '@/components/deploymentAutocomplete/KeyboardDeploymentAutocomplete.vue';
    import {ToolTipData, DeploymentDragData} from '@/types';
    import {PersonalStampShift, Unavailability, PersonalPlanningWish, Shift, PersonalStamp, Deployment} from '@cquential/api';
    import {RosterDay, WrDragData} from '@/types/types';
    import {ResourceWithKeyedByDateObjects} from '@/types/api-object-extensions';
    import DeploymentUtil from '@/util/deploymentutil';

    @Component({
        components: {
            StampShiftMarker,
            StampShiftAddButton,
        },
        emits: ['create:stampShift'],
    })
    export default class StampCell extends Vue {
        @Prop({required: true}) public stampShifts!: PersonalStampShift[];
        @Prop({required: true}) public day!: RosterDay;
        @Prop({type: Number, required: true}) public dayIndex!: number;
        @Prop() public resource?: ResourceWithKeyedByDateObjects;
        @Prop({type: Number}) public resourceIndex?: number;
        @Prop() public stamp?: PersonalStamp;
        @Prop({type: Number}) public stampDayIndex?: number;
        @Prop({type: Boolean}) public isEmployed!: boolean;
        @Prop({required: true}) public rosterNames!: Record<number, string>;
        @Prop({type: Number, required: true}) public rosterId!: number;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({type: Boolean}) public editable!: boolean;
        @Prop({type: Boolean}) public fromOtherRoster!: boolean;
        @Prop() public wish?: PersonalPlanningWish;
        @Prop() public unavailabilities?: Unavailability[];

        @Ref() public readonly root!: HTMLElement;

        public handleDragDropEvents(event: DragEvent): void {
            switch (event.type) {
                // Receiving data (dragover, dragleave, drop)
                case 'dragover':
                    // Add class on dragover
                    this.root.classList.add('dragover');
                    /** @see Tooltip.closeTooltip() */
                    // @ts-expect-error TS2339
                    Tooltip.__vfdConstructor.closeTooltip();
                    /** @see KeyboardDeploymentAutocomplete.disable() */
                    // @ts-expect-error TS2339
                    KeyboardDeploymentAutocomplete.__vfdConstructor.disable();
                    break;
                case 'dragleave':
                    // Remove class on dragleave
                    this.root.classList.remove('dragover');
                    break;
                case 'drop':
                    if (this.resource && this.stamp && this.stamp.id) {
                        this.root.classList.remove('dragover');
                        // Fire method to take further action
                        if (event.target instanceof HTMLElement && event.dataTransfer) {
                            let parsedData: WrDragData | DeploymentDragData;
                            try {
                                parsedData = JSON.parse(event.dataTransfer.getData('text'));
                            } catch {
                                event.preventDefault();
                                break;
                            }
                            const deployment: Deployment = DeploymentUtil.convertDeploymentDragDataToRosterDeployment(parsedData);
                            const stampShift: PersonalStampShift = {
                                stampId: this.stamp.id,
                                dayIndex: this.stampDayIndex!,
                                shiftId: deployment.shiftId,
                            };

                            this.$emit('create:stampShift', {shift: stampShift, resourceId: this.resource.id!});
                        }
                    }
                    break;
            }
        }

        public enableTooltip(event: Event): void {
            if (this.resource && this.stamp) {
                const tooltip: ToolTipData = {
                    resource: this.resource,
                    day: this.day.date,
                    event,
                    stamp: this.stamp,
                    type: 'personalStamp',
                };
                /** @see Tooltip.setTooltip() */
                // @ts-expect-error TS2339
                Tooltip.__vfdConstructor.setTooltip(tooltip);
            }
        }

        public disableTooltip(): void {
            /** @see Tooltip.closeTooltip() */
            // @ts-expect-error TS2339
            Tooltip.__vfdConstructor.closeTooltip();
        }
    }
</script>

<style lang="scss" scoped>
    .stamp-holder {
        &:only-child {
            height: 100%;
        }

        &.dragover {
            background: #aaa;
        }
    }
</style>
